import { useIsDocumentInboxEnabled } from '@/app-shell/flags/integrations-flags';
import { posthog } from '@/app-shell/flags/posthog-compat';
import { useBooleanFlagValue } from '@openfeature/react-sdk';

/**
 * Clinical tasks inside the document inbox. Sub-flag of the document-inbox
 * umbrella so the surface can be rolled back independently of the inbox.
 * Rollout/kill gate — do not add `useManualExposure`.
 *
 * STAYS APP-SIDE — ruling 36 phase 2, classified rather than
 * skipped. `release-ml-scribe-enable-inbox-clinical-tasks` is a `@heidi/tasks`
 * flag by noun, but the body ANDs `useIsDocumentInboxEnabled()` — a FOREIGN
 * domain's getter (integrations / document-inbox). Ruling 36 (b) refuses that
 * import outright: a package would bind another domain's whole door for one
 * boolean and invert the dependency direction for it, and the resolved boolean
 * crosses as a prop at the composition point that already owns it. So the
 * composition stays here, where it is legal, and the forceAll branch stays with
 * it.
 *
 * The move this unblocks is not a package move: it is inverting the composition
 * so the inbox boolean is supplied at the composition point rather than read
 * inside the getter. That is a call-site change at the one production consumer,
 * and a separate decision from this batch.
 */
export const useIsInboxClinicalTasksEnabled = (): boolean => {
  const isInboxEnabled = useIsDocumentInboxEnabled();
  const forceAll = process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
  const fromFlagsmith = useBooleanFlagValue(
    'release-ml-scribe-enable-inbox-clinical-tasks',
    false
  );

  return isInboxEnabled && (forceAll || fromFlagsmith);
};

export const IS_CLINICAL_TASKS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks') ?? false;
};

export const IS_CLINICAL_TASKS_GENERATE_DOCUMENT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks-generate-document');
};

/**
 * Gates the Evidence CTA on document-category clinical tasks. When on,
 * the task row shows a FileText-glyphed Evidence button that opens the
 * in-session Evidence chat sidebar and auto-submits the task title
 * (with the source_quote as context) so Heidi drafts the document using
 * the consultation transcript as evidence.
 */
export const IS_CLINICAL_TASKS_EVIDENCE_CTA_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks-evidence-cta') ?? false;
};

export const IS_TASK_GENERATION_BG_AUTOGEN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-task-generation-bg-autogen') ?? false;
};
